import type { ReactNode } from "react";
import { PageHeader } from "@/components/ui/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

/** Placeholder rows shaped like a table, shown while a list loads. */
export function TableSkeleton({ rows = 6, columns = 4, className }: { rows?: number; columns?: number; className?: string }) {
  return (
    <div className={cn("overflow-hidden rounded-lg border border-border/60", className)} aria-busy="true">
      <div className="flex gap-4 border-b border-border/60 bg-surface-2 px-4 py-2.5">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-3 flex-1 bg-muted/60" />
        ))}
      </div>
      <div className="divide-y divide-border/60">
        {Array.from({ length: rows }).map((_, row) => (
          <div key={row} className="flex gap-4 px-4 py-3.5">
            {Array.from({ length: columns }).map((_, col) => (
              <Skeleton key={col} className={cn("h-4 flex-1 bg-muted/50", col === 0 && "max-w-[40%]")} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Header, figures and a table: the shape of most back-office pages. With a
 * title, the real page header shows while the rest loads.
 */
export function ListPageSkeleton({ stats = 3, title, description }: { stats?: number; title?: ReactNode; description?: ReactNode }) {
  return (
    <div className="mx-auto w-full max-w-7xl space-y-6" aria-busy="true">
      {title ? (
        <PageHeader title={title} description={description} />
      ) : (
        <div className="space-y-2 border-b border-border/60 pb-6">
          <Skeleton className="h-7 w-64 bg-muted/60" />
          <Skeleton className="h-4 w-96 max-w-full bg-muted/40" />
        </div>
      )}
      {stats > 0 ? (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {Array.from({ length: stats }).map((_, i) => (
            <Skeleton key={i} className="h-16 bg-muted/40" />
          ))}
        </div>
      ) : null}
      <TableSkeleton />
    </div>
  );
}
